import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, X, FileJson, FileText, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { customerService } from "@/services";
import { toast } from "sonner";

interface ExportDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ExportDataDialog: React.FC<ExportDataDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState<"json" | "csv" | null>(null);

  const handleExport = async (format: "json" | "csv") => {
    setIsExporting(format);
    toast.info(t("profile.export.start", "Generating your data file..."));
    try {
      await customerService.exportProfile(format);
      toast.success(t("profile.export.success", "Data downloaded successfully."));
      onOpenChange(false);
    } catch (error) {
      toast.error(t("profile.export.error", "Failed to download data. Please try again."));
    } finally {
      setIsExporting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isExporting ? undefined : onOpenChange}>
      <DialogContent
        hideCloseButton
        className="sm:max-w-md w-[calc(100%-32px)] rounded-[20px] p-6 border-none shadow-xl gap-0 outline-none"
      >
        {/* Close Button at top-right */}
        <DialogClose
          disabled={!!isExporting}
          className="absolute right-4 top-4 rounded-md p-1 outline-none focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors border-2 border-transparent bg-[#F9F9FA] text-black hover:bg-gray-200 disabled:opacity-50"
        >
          <X className="w-[18px] h-[18px] stroke-[2.5]" />
        </DialogClose>

        {/* Icon */}
        <div className="flex justify-center mb-4 mt-2">
          <div className="w-[64px] h-[64px] rounded-[20px] bg-[#EEF2F6] flex items-center justify-center">
            <Download className="w-8 h-8 text-[#6B4EFF]" strokeWidth={1.5} />
          </div>
        </div>

        <DialogHeader className="space-y-3 px-2">
          <DialogTitle className="text-[20px] font-bold text-center text-[#101828]">
            {t("profile.export.title", "Export Data")}
          </DialogTitle>
          <DialogDescription className="text-[14px] leading-[1.6] text-center text-[#475467]">
            {t(
              "profile.export.description",
              "Choose a format to download all your personal data and transaction history.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-8 px-1 w-full flex flex-col gap-4">
          <Button
            type="button"
            disabled={!!isExporting}
            onClick={() => handleExport("csv")}
            className="w-full rounded-[10px] h-[52px] bg-[#6B4EFF] hover:bg-[#5a42d6] disabled:bg-[#a99bf8] disabled:opacity-100 font-semibold text-white transition-colors text-[16px] shadow-sm flex items-center justify-center gap-2"
          >
            {isExporting === "csv" ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <FileText className="w-5 h-5" />
            )}
            {t("profile.export.csv_action", "Export to CSV")}
          </Button>

          <Button
            type="button"
            disabled={!!isExporting}
            onClick={() => handleExport("json")}
            variant="outline"
            className="w-full rounded-[10px] h-[52px] border-2 border-[#E4E7EC] hover:bg-[#F9F9FA] text-[#101828] font-semibold transition-colors text-[16px] flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isExporting === "json" ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <FileJson className="w-5 h-5" />
            )}
            {t("profile.export.json_action", "Export to JSON")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

ExportDataDialog.displayName = "ExportDataDialog";
